/**
 * useSessionHistory — 加载历史会话消息
 * 从 OpsMateContext 中抽取
 */
import { useEffect, useRef } from "react";
import { App } from "antd";
import { searchSessionLog } from "@/services/opsmate/sessionLog";
import { getDocumentsByIds } from "@/services/opsmate/documents";
import {
  isHistoryConversation,
  getReferenceRelatedDocIds,
  matchDocInfoByRelatedIds,
  getDocInfoDocumentGuid,
  getDocInfoDataSource,
  getDocInfoCategoryIds,
  getDocInfoSourcePathAskId,
  isDocInfoValid,
} from "@/utils/conversation";
import { formatFileSize } from "@/utils/formatters";
import { splitReferencesFence } from "@/utils/referencesFence";
import { filterDisplayProgressSteps } from "@/utils/progressSteps";
import { buildSessionLogItems } from "@/hooks/opsmate/sessionLogItems";

interface UseSessionHistoryParams {
  selectedKeys: string;
  pendingConversationId: string | null;
  setItems: React.Dispatch<React.SetStateAction<any[]>>;
  setDocsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  setManualSources: React.Dispatch<React.SetStateAction<any[]>>;
  /** 读取指定会话的 items（stable ref，不触发额外 effect re-run） */
  getItemsForConversation: (convId: string) => any[];
}

export function useSessionHistory({
  selectedKeys,
  pendingConversationId,
  setItems,
  setDocsCollapsed,
  setManualSources,
  getItemsForConversation,
}: UseSessionHistoryParams) {
  const { message } = App.useApp();
  const messageRef = useRef(message);
  messageRef.current = message;

  useEffect(() => {
    if (!isHistoryConversation(selectedKeys)) {
      // 切回 New Ask 时清空消息
      if (selectedKeys === "add") {
        setItems([]);
        setManualSources([]);
        setDocsCollapsed(true);
      }
      return;
    }

    // 如果是 pending 的新建会话（刚提问、还没返回），不要覆盖当前 items
    if (pendingConversationId && String(pendingConversationId) === String(selectedKeys)) {
      return;
    }

    // 会话已有内容（可能正在后台流式生成），跳过从服务端加载以避免覆盖
    if (getItemsForConversation(selectedKeys).length > 0) {
      return;
    }

    const loadSessionLogs = async () => {
      const conversationId = selectedKeys;
      if (!conversationId) {
        setItems([]);
        return;
      }
      let loading = messageRef.current.loading({
        content: "Loading...",
        key: "load-history",
        duration: 0,
      });
      try {
        const res: any = await searchSessionLog({
          ConversationID: conversationId,
          Page: 1,
          Limit: 100,
        });

        const rawList: any[] =
          (Array.isArray(res?.Data) && res.Data) ||
          (Array.isArray(res?.data) && res.data) ||
          (Array.isArray(res) && res) ||
          [];

        if (!rawList.length) {
          if (pendingConversationId && String(pendingConversationId) === String(conversationId)) {
            return;
          }
          setItems([]);
          return;
        }

        const mapped = await buildSessionLogItems(rawList);
        setItems(mapped.items);
        setDocsCollapsed(!mapped.hasDocuments);
        return;

        type RowDoc = {
          name: string;
          citations: string;
          canDownload: boolean;
          id?: string;
          segmentNums: number[];
          /** 该引用关联的全部文档 ID（relatedDocumentIds，含 owner 与重复件），用于命中 DB 文档 */
          relatedIds: string[];
        };
        const refDocIdSet = new Set<string>();

        // QuestionType=1 为用户问题，QuestionType=0 为 AI 答案
        const messageRecords: Array<{
          baseKey: string;
          role: "user" | "ai";
          content: string;
          execMsg?: any;
          summary?: string;
          docMap?: Map<string, RowDoc>;
          references?: any[];
          rationale?: string;
          progress?: any[];
          feedback?: "like" | "dislike" | "default";
          translatedSummary?: string;
          translatedAnswer?: string;
          translatedRationale?: string;
        }> = [];

        rawList.forEach((row: any, index: number) => {
          const entity = row?.Entity ?? row ?? {};

          const qType = String(
            entity.SessionLog_QuestionType ??
              entity.sessionlog_questiontype ??
              entity.QuestionType ??
              "",
          );

          const baseKey: string = String(
            entity.ID ?? entity.Id ?? entity.id ?? entity.SessionLog_ID ?? index,
          );

          // 用户问题行（QuestionType = 1）
          if (qType === "1") {
            const questionText: string =
              entity.SessionLog_Question ??
              entity.SessionLog_question ??
              entity.Question ??
              entity.question ??
              "";
            if (questionText) {
              messageRecords.push({
                baseKey,
                role: "user",
                content: String(questionText),
              });
            }
            return;
          }

          // AI 答案行（QuestionType = 0）
          if (qType === "0") {
            const rawAnswerText: string =
              entity.SessionLog_Answer ??
              entity.SessionLog_answer ??
              entity.Answer ??
              entity.answer ??
              "";

            // 剥离内联 ```references 围栏：兼容修复前已落库、Answer 里仍含引用 JSON 的历史数据，
            // 避免刷新会话时把引用 JSON 当代码块原样显示（引用清单仍由 Quote 提供）。
            const answerText = splitReferencesFence(String(rawAnswerText ?? "")).text;
            const rationaleFromAnswer: string = String(
              entity.SessionLog_Rationale ??
                entity.SessionLog_rationale ??
                entity.Rationale ??
                entity.rationale ??
                "",
            );

            // 点赞/点踩状态
            const praiseValueRaw =
              entity.SessionLog_UserIsPraiseValue ??
              entity.sessionlog_userispraisevalue ??
              entity.UserIsPraise ??
              entity.userispraise ??
              0;
            const praiseValue = Number(praiseValueRaw);
            const feedback: "like" | "dislike" | "default" =
              praiseValue === 1 ? "like" : praiseValue === -1 ? "dislike" : "default";

            // 解析引用: 优先 SessionLog_references，其次从 SessionLog_Quote（JSON 字符串）解析
            let references: any[] = [];
            const rawRefs = entity.SessionLog_references ?? entity.sessionlog_references;
            if (Array.isArray(rawRefs)) {
              references = rawRefs;
            } else {
              const quoteRaw = entity.SessionLog_Quote ?? entity.SessionLog_quote ?? "";
              if (typeof quoteRaw === "string" && quoteRaw.trim()) {
                try {
                  const parsed = JSON.parse(quoteRaw);
                  if (Array.isArray(parsed)) references = parsed;
                } catch {
                  /* ignore */
                }
              }
            }

            const docMap = new Map<string, RowDoc>();
            (references || []).forEach((ref: any, idx: number) => {
              const segmentNum = idx + 1;
              const docId = ref?.references_document_id ?? ref?.document_id ?? "";
              // 用 relatedDocumentIds 查 getdocumentid：owner 未必落库，重复件才在本租户 DB 里。
              const relatedIds = getReferenceRelatedDocIds(ref);
              relatedIds.forEach((rid) => refDocIdSet.add(rid));
              const key = docId || "unknown";

              const rawSegment = (ref?.references_segment ?? "").toString();
              let seg = rawSegment;
              const marker = "Content: ";
              const markerIndex = rawSegment.indexOf(marker);
              if (markerIndex >= 0) seg = rawSegment.slice(markerIndex + marker.length);

              const pos = ref?.references_segment_position ?? ref?.segment_position ?? "";
              const line = pos ? `Page ${pos}: ${seg}` : seg;

              const existed = docMap.get(key);
              if (!existed) {
                docMap.set(key, {
                  id: docId,
                  name: docId || "未知文档",
                  citations: line,
                  canDownload: true,
                  segmentNums: [segmentNum],
                  relatedIds,
                });
              } else {
                existed.segmentNums.push(segmentNum);
                relatedIds.forEach((rid) => {
                  if (!existed.relatedIds.includes(rid)) existed.relatedIds.push(rid);
                });
                if (line) existed.citations = `${existed.citations}\n\n---\n\n${line}`;
                docMap.set(key, existed);
              }
            });

            // AI Reasoning 区域显示 rationale 内容。
            // 历史记录中 rationale 是从落库的 answer JSON 字符串里解析出来的（见上方 parsedHistoryAnswer）。
            const rationaleText = rationaleFromAnswer;

            // 解析推理 / 检索过程（与前端 ProgressStep[] 形状对齐，由后端 SessionLogService 收集）
            let progressSteps: any[] = [];
            const rawProgress =
              entity.SessionLog_ProgressLog ?? entity.SessionLog_progresslog ?? entity.ProgressLog;
            if (Array.isArray(rawProgress)) {
              progressSteps = rawProgress;
            } else if (typeof rawProgress === "string" && rawProgress.trim()) {
              try {
                const parsed = JSON.parse(rawProgress);
                if (Array.isArray(parsed)) progressSteps = parsed;
              } catch {
                /* ignore: 非法 JSON 时静默丢弃，不阻塞历史加载 */
              }
            }
            // 与会话区实时流式展示口径一致：丢弃进度提示、仅保留带命中文档名的检索结果 trace
            progressSteps = filterDisplayProgressSteps(progressSteps);

            // Summary 区域只展示后端保存的 summary 字段本身，没有则留空（不再回退到 rationale）。
            // 目前后端历史落库中未单独存储 summary 字段，历史会话的 Summary 区域会为空。
            const summaryFromHistory = String(
              entity.SessionLog_Summary ??
                entity.SessionLog_summary ??
                entity.Summary ??
                entity.summary ??
                "",
            );
            let execMsg: Record<string, unknown>;
            try {
              execMsg = JSON.parse(entity?.SessionLog_ExecutionMessage ?? "{}");
            } catch {
              execMsg = {};
            }
            const translatedSummary = String(
              entity.SessionLog_TranslatedSummary ??
                entity.SessionLog_translatedsummary ??
                entity.TranslatedSummary ??
                "",
            );
            const translatedAnswer = String(
              entity.SessionLog_TranslatedAnswer ??
                entity.SessionLog_translatedanswer ??
                entity.TranslatedAnswer ??
                "",
            );
            const translatedRationale = String(
              entity.SessionLog_TranslatedRationale ??
                entity.SessionLog_translatedrationale ??
                entity.TranslatedRationale ??
                "",
            );

            messageRecords.push({
              baseKey,
              role: "ai",
              content: String(answerText),
              execMsg,
              summary: summaryFromHistory,
              docMap,
              references,
              rationale: rationaleText,
              progress: progressSteps,
              feedback,
              translatedSummary,
              translatedAnswer,
              translatedRationale,
            });
          }
        });

        // 批量获取文档信息（循环外统一调用一次）
        let docInfoList: any[] = [];
        if (refDocIdSet.size > 0) {
          try {
            const docIds = Array.from(refDocIdSet);
            const docInfoResp: any = await getDocumentsByIds(docIds);
            docInfoList =
              (Array.isArray(docInfoResp?.data) && docInfoResp.data) ||
              (Array.isArray(docInfoResp?.Data) && docInfoResp.Data) ||
              (Array.isArray(docInfoResp) && docInfoResp) ||
              [];
          } catch (e) {
            console.error("getDocumentsByIds (history) failed", e);
          }
        }

        const nextItems: any[] = [];

        for (const rec of messageRecords) {
          const documents = rec.docMap
            ? Array.from(rec.docMap.entries()).map(([, doc]) => {
                const matchedInfo = matchDocInfoByRelatedIds(docInfoList, doc.relatedIds, doc.name);

                // 不在 DB DocumentList 中：仍保留在 documents（正文角标 / References 区需要展示），
                // 但不打 inDocumentList 标记，右侧边栏据此过滤
                if (!matchedInfo)
                  return {
                    id: doc.id,
                    name: doc.name,
                    citations: doc.citations,
                    canDownload: doc.canDownload,
                    segmentNums: doc.segmentNums,
                  };

                const rawSize = matchedInfo.FileSize ?? matchedInfo.fileSize ?? matchedInfo.size;
                const size = formatFileSize(rawSize);
                const uploadedAt =
                  matchedInfo.CreateTime ??
                  matchedInfo.FileCreateTime ??
                  matchedInfo.FileUpdateTime ??
                  matchedInfo.uploadTime ??
                  matchedInfo.uploadedAt ??
                  matchedInfo.createTime;
                const fileUrl = matchedInfo.DownloadURL;
                const displayName =
                  matchedInfo.FileName ??
                  matchedInfo.fileName ??
                  matchedInfo.documentName ??
                  matchedInfo.DocumentName ??
                  doc.name;

                // 右侧文档清单完全以 getdocumentid 校准回的 DocumentGuid 为准：写入 guidName
                // （getDocumentGuid 优先取它），用于去重与 Only use selected sources 的 references_document_id。
                // 保留 doc.id（上游 owner 文档 ID）不动：正文角标命中与 artifact 图片鉴权仍按 owner ID。
                return {
                  id: doc.id,
                  guidName: getDocInfoDocumentGuid(matchedInfo) || undefined,
                  sourcePathAskId: getDocInfoSourcePathAskId(matchedInfo) || undefined,
                  docCategoryIds: getDocInfoCategoryIds(matchedInfo),
                  name: displayName ? String(displayName) : doc.name,
                  citations: doc.citations,
                  canDownload: doc.canDownload,
                  segmentNums: doc.segmentNums,
                  size: size || undefined,
                  uploadedAt: uploadedAt ? String(uploadedAt) : undefined,
                  fileUrl: fileUrl ? String(fileUrl) : undefined,
                  dataSource: getDocInfoDataSource(matchedInfo) || undefined,
                  // 软删旧行（IsValid=false）仍保留 enrichment 供下载/References，但不进右侧边栏
                  inDocumentList: isDocInfoValid(matchedInfo),
                };
              })
            : [];

          if (rec.role === "user") {
            nextItems.push({
              key: `${rec.baseKey}-q`,
              role: "user",
              content: rec.content,
            });
          } else {
            nextItems.push({
              key: `${rec.baseKey}-a`,
              role: "ai",
              status: "success",
              content: rec.content,
              summary: rec.summary ?? "",
              rationale: rec.rationale ?? "",
              execMsg: rec.execMsg ?? {},
              documents,
              references: rec.references ?? [],
              progress: Array.isArray(rec.progress) ? rec.progress : [],
              extraInfo: {
                feedback: rec.feedback ?? "default",
                sessionLogId: rec.baseKey,
                ...(rec.translatedSummary || rec.translatedAnswer || rec.translatedRationale
                  ? {
                      isTranslated: false,
                      ...(rec.translatedSummary
                        ? { translatedSummary: rec.translatedSummary }
                        : {}),
                      ...(rec.translatedAnswer ? { translatedText: rec.translatedAnswer } : {}),
                      ...(rec.translatedRationale
                        ? { translatedRationale: rec.translatedRationale }
                        : {}),
                    }
                  : {}),
              },
            });
          }
        }

        setItems(nextItems);
        const hasDocuments = nextItems.some(
          (m: any) => Array.isArray(m.documents) && m.documents.length > 0,
        );
        setDocsCollapsed(!hasDocuments);
      } catch (error) {
        console.error("Failed to load session logs", error);
        // 出错时保持现有内容，避免影响当前对话
      } finally {
        loading?.(); // 关闭 loading
      }
    };

    loadSessionLogs();
    // pendingConversationId 故意不加入依赖数组，防止 cancel 后触发重新加载历史
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys, setItems, setDocsCollapsed, setManualSources, getItemsForConversation]);
}
